import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FormFieldType, IndFormFieldViewDataModel } from '@omni/models/indFormFieldDataModel';
import { NavigationService } from '@omni/services/navigation/navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { OffTakeCheckService } from '@omni/services/offtake-check/offtake-check.service';
import { Account } from '@omni/classes/account/account.class';
import { OffTakeCheck } from '@omni/classes/offtake-check/offtake-check.class';
import { IndSectionHeaderViewDataModel } from '@omni/models/indSectionHeaderDataModel';
import { IndPageTitleViewDataModel } from '@omni/models/indPageTitleDataModel';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { DatePipe } from '@angular/common';
import { AuthenticationService } from '@omni/services/authentication.service';
import { AlertService } from '@omni/services/alert/alert.service';
import { AlertInfoTypes, OmniInfoAlertComponent } from '@omni/components/shared/omni-info-alert/omni-info-alert.component';
import { DeviceService } from '@omni/services/device/device.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { cloneDeep, isEmpty, sortBy } from 'lodash-es';

@Component({
  selector: 'app-offtake-check',
  templateUrl: './offtake-check.component.html',
  styleUrls: ['./offtake-check.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfftakeCheckComponent implements OnInit {
  @Input() selectedAccount: Account;
  offTakeCheck: OffTakeCheck;
  public pageTitle: IndPageTitleViewDataModel;
  yearSectionHeader: IndSectionHeaderViewDataModel;
  selectedDate: Date;
  months: { id: number, name: string }[];
  isSavedEnabled: boolean = false;
  constructor(public modalCtrl: ModalController,
    public device: DeviceService,
    private notificationService: NotificationService,
    public navService: NavigationService,
    public offtakeCheckService: OffTakeCheckService,
    public readonly translate: TranslateService,
    private datePipe: DatePipe,
    private dateTimeFormatsService: DateTimeFormatsService,
    private authService: AuthenticationService,
    private alertService: AlertService,
    public _modalCtrl: ModalController,
    private readonly _cd: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.selectedDate = this.dateTimeFormatsService.getStartOfMonth(new Date());
    this.initPageTitle();
    this.offtakeCheckService.getOfftakeCheckData(this.selectedAccount.id, this.selectedDate).then(() => {
      this.setOffTakeCheck()
      if (!this.offTakeCheck) {
        this.offTakeCheck = this.offtakeCheckService.getEmptyOfftakeCollection(this.selectedDate, this.selectedAccount.id);
      }
      this._cd.markForCheck();
    });
    this.yearSectionHeader = {
      id: 'year-header',
      title: this.selectedDate.getFullYear() + '',
      controls: []
    }
    this.months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, index) => {
      return {
        id: index,
        name: this.translate.instant(m.toUpperCase()).toUpperCase()
      }
    });
  }

  private initPageTitle() {
    this.pageTitle = {
      id: 'offtake',
      isHCPFacing: true,
      title: this.translate.instant('OFFTAKECHECK_CAPS') + " - " + this.selectedAccount?.accountName,
      controls: [{
        id: "close",
        icon: "chevron-back-outline",
        isDisabled: false,
        align: "left"
      },
      {
        id: "save",
        icon: 'checkmark',
        name: this.translate.instant('SAVE'),
        isDisabled: !this.isSavedEnabled,
        align: "right"
      }
      ]
    };
  }

  public async pageTitleControlClick(id) {
    if (id === 'close') {
      if (!await this.discardPopup()) return;
      const currentMonthData = this.offtakeCheckService.offTakeChecks.find(offTake => offTake.indskr_offtakedate.getTime() === this.dateTimeFormatsService.getStartOfMonth(new Date()).getTime());
      this.modalCtrl.dismiss({currentMonthData}).then(() => this.navService.isModalViewOpen = false);
    } else if (id === 'save') {
      if(this.device.isOffline){
        let toastMsg = this.translate.instant("OFFLINE_OFFTAKE_CLICK_SAVE")
        .replace('{{month}}', this.translate.instant(this.dateTimeFormatsService.formatDate(this.selectedDate, 'MMM').toUpperCase()))
        .replace('{{year}}', this.selectedDate.getFullYear());
        this.notificationService.notify(toastMsg, "Meeting Details", "top", ToastStyle.DANGER , 3000);
      }
      this.offtakeCheckService.saveOffTakeCollection(this.offTakeCheck).then((savedOffTakeCheck) => {
        if (savedOffTakeCheck) {
          this.updateNotification();
          this.offTakeCheck = savedOffTakeCheck;
          this.isSavedEnabled = false;
          this.initPageTitle();
        }
        this._cd.markForCheck();
      });
    }
  }

  public getModifiedByFormField(): IndFormFieldViewDataModel {
    let viewData: IndFormFieldViewDataModel;
    viewData = {
      label: this.translate.instant('MODIFIED_BY'),
      inputText: isEmpty(this.offTakeCheck?.modifiedby) ? '-' : this.offTakeCheck.modifiedby,
      id: 'modified-by-field',
      isReadOnly: true,
      isDisabled: true,
      showArrow: false,
    };
    return viewData;
  }

  public getModifiedDateFormField(): IndFormFieldViewDataModel {
    let viewData: IndFormFieldViewDataModel;
    viewData = {
      label: this.translate.instant('MODIFIED_DATE'),
      inputText: this.offTakeCheck?.modifiedOn ? this.datePipe.transform(this.offTakeCheck.modifiedOn, this.dateTimeFormatsService.date, undefined, this.translate.currentLang) : '-',
      id: 'modified-date-field',
      isReadOnly: true,
      isDisabled: true,
      showArrow: false,
    };
    return viewData;
  }

  async setCurrentMonth(month) {
    if (!await this.discardPopup()) return;
    this.selectedDate = this.dateTimeFormatsService.getStartOfMonth(new Date(this.selectedDate.getFullYear(), month.id));
    this.setOffTakeCheck();
  }

  setOffTakeCheck() {
    this.offTakeCheck = cloneDeep(this.offtakeCheckService.offTakeChecks.find(offTake => offTake.indskr_offtakedate.getTime() === this.selectedDate.getTime()));
    if (this.isEditable) {
      if (this.offTakeCheck) {
        const emptyOffTake = this.offtakeCheckService.getEmptyOfftakeCollection(this.selectedDate, this.selectedAccount.id);
        emptyOffTake.collections.forEach(offTakeCollection => {
          if (!this.offTakeCheck.collections.find(coll => coll.productId === offTakeCollection.productId && coll.skuId === offTakeCollection.skuId)) {
            this.offTakeCheck.collections.push(offTakeCollection);
          }
        })
        this.offTakeCheck.collections = sortBy(this.offTakeCheck.collections, ['productName', 'skuName']);
      } else {
        this.offTakeCheck = this.offtakeCheckService.getEmptyOfftakeCollection(this.selectedDate, this.selectedAccount.id);
      }
    }
  }

  get isEditable() {
    const monthsDiff = this.dateTimeFormatsService.getDifferenceInMonths(new Date(), this.selectedDate);
    return monthsDiff > 0 ? this.authService.user.buConfigs?.indskr_offtakecheckpreviousmonthlimit > 0 && (monthsDiff <= this.authService.user.buConfigs.indskr_offtakecheckpreviousmonthlimit) : true;
  }

  private async updateNotification() {
    const modal = await this._modalCtrl.create({
      component: OmniInfoAlertComponent,
      cssClass: 'omni-notification-modal',
      componentProps: {
        header: this.translate.instant('OFFTAKE_UPDATED'),
        alertType: AlertInfoTypes.NOTIFICATION_MESSAGE,
        notificationMessage: this.translate.instant("OFFTAKE_SUBMITTED")
          .replace('{{month}}', this.translate.instant(this.dateTimeFormatsService.formatDate(this.selectedDate, 'MMM').toUpperCase()))
          .replace('{{year}}', this.selectedDate.getFullYear())
      }
    });
    await modal.present();

    await modal.onDidDismiss().then(({ data }) => {
    })
  }

  getMonthColorWithBackGround(month) {
    // console.log(month)
    const date = this.dateTimeFormatsService.getStartOfMonth(new Date(this.selectedDate.getFullYear(), month.id));
    if (this.dateTimeFormatsService.getStartOfMonth(new Date()).getTime() === date.getTime()) {
      return 'currentMonth'
    }
    if (date > new Date()) {
      //Future
      return 'futureMonth';
    }
    else if (this.offtakeCheckService.offTakeChecks.some(offTake => offTake.indskr_offtakedate.getTime() === date.getTime())) {
      //Past/Present
      return 'dataAvailable';
    } else {
      return 'noDataAvailable';
    }
  }

  onUpdate(event) {
    this.isSavedEnabled = true;
    if(this.device.isOffline){
      this.isSavedEnabled = false ;
      this.notificationService.notify(this.translate.instant('OFFLINE_OFFTAKE_CLICK_ADD'), "Meeting Details", "top", ToastStyle.DANGER, 3000);
    }
    this.initPageTitle();
  }

  async prevYear() {
    if (!await this.discardPopup()) return;
    // if(this.selectedDate.getFullYear() == new Date().getFullYear) {
    this.selectedDate = this.dateTimeFormatsService.getStartOfMonth(new Date(this.selectedDate.getFullYear() - 1, 11));
    // }
    this.offtakeCheckService.getOfftakeCheckData(this.selectedAccount.id, this.selectedDate).then(() => {
      this.setOffTakeCheck()
      if (!this.offTakeCheck && this.isEditable) {
        this.offTakeCheck = this.offtakeCheckService.getEmptyOfftakeCollection(this.selectedDate, this.selectedAccount.id);
      }
      this._cd.markForCheck();
    });
  }

  async nextYear() {
    if (!await this.discardPopup()) return;
    if (this.selectedDate.getFullYear() === new Date().getFullYear()) {
      return; // No Data will be available for Future
    }
    if (this.selectedDate.getFullYear() < new Date().getFullYear()) {
      //Go untill current year
      this.selectedDate = this.dateTimeFormatsService.getStartOfMonth(new Date(this.selectedDate.getFullYear() + 1, 11));
      if (this.selectedDate.getTime() > this.dateTimeFormatsService.getStartOfMonth(new Date()).getTime()) {
        //Future month ? -> set to current month
        this.selectedDate = this.dateTimeFormatsService.getStartOfMonth(new Date());
      }
      this.offtakeCheckService.getOfftakeCheckData(this.selectedAccount.id, this.selectedDate).then(() => {
        this.setOffTakeCheck()
        if (!this.offTakeCheck && this.isEditable) {
          this.offTakeCheck = this.offtakeCheckService.getEmptyOfftakeCollection(this.selectedDate, this.selectedAccount.id);
        }
        this._cd.markForCheck();
      });
    }
  }

  trackByMonthId(index: number, item: any) {
    return item?.id ?? index;
  }

  async discardPopup() {
    if (this.isSavedEnabled) {
      const res = await this.alertService.showAlert({
        title: this.translate.instant('DISCARD_CHANGES'),
        message: this.translate.instant('CI_R_U_WANT_DISCARD_CHANGES')
      }, this.translate.instant('DISCARD')
      );
      if (res.role == "ok") {
        this.setOffTakeCheck()
        if (!this.offTakeCheck && this.isEditable) {
          this.offTakeCheck = this.offtakeCheckService.getEmptyOfftakeCollection(this.selectedDate, this.selectedAccount.id);
        }
        this.isSavedEnabled = false;
        this.initPageTitle();
        return true;
      } else {
        return false;
      }
    }
    return true;
  }
}